import React, {memo, useEffect, useRef, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import {ClipboardPaste, Plus} from "lucide-react";
import {FlowMode, FlowNodeTypes} from "@/types/node";
import {FlowNodesData} from "../NodeTypes/NodesData";
import {Edge, useReactFlow} from "@xyflow/react";
import {useSave} from "@/contexts/SaveContext";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {useCopyPasteContext} from "@/contexts/CopyPasteContext";
import {getTailwindColors} from "@/constants/tailwindConfigs";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";

interface NewNodeHandleProps {
  id: string;
  onNewNodeSelect: (blueprint: any) => void;
  selectedEdge: Edge;
}

const NewNodeHandle: React.FC<NewNodeHandleProps> = ({
  id,
  onNewNodeSelect,
  selectedEdge,
}) => {
  const {handleSave, flowMode} = useSave();
  const {bufferedNodes} = useCopyPasteContext();
  const {getNode, getEdges, getNodes} = useReactFlow();
  const TailwindColors = getTailwindColors();

  const menuRef = useRef<HTMLDivElement>(null);
  const isDemoMode = FlowBuilderSettings.demoMode;

  const edge = getEdges().find(edge => edge.id === id);
  const [isPasteAvailable, setIsPasteAvailable] = useState(
    bufferedNodes.length > 0,
  );
  const isMenuVisible = id === selectedEdge?.id;

  const targetNode = getNode(edge?.target) || null;
  const sourceNode = getNode(edge?.source) || null;

  const isTargetPlaceholder = targetNode?.type === FlowNodeTypes.PLACEHOLDER;
  const isSourceStart = sourceNode?.type === FlowNodeTypes.START;
  const isSourceEntry = sourceNode?.type === FlowNodeTypes.ENTRY;

  const defaultNodeTypes = [
    FlowNodeTypes.COLLECT_VARIABLES,
    FlowNodeTypes.BRANCH,
    FlowNodeTypes.ACTION,
    FlowNodeTypes.TRANSFER,
    FlowNodeTypes.BOOKING,
    FlowNodeTypes.SPEAK,
  ];

  const availableNodeTypes = [...defaultNodeTypes];

  if (isTargetPlaceholder && !isSourceStart) {
    availableNodeTypes.push(FlowNodeTypes.JUMP);
    availableNodeTypes.push(FlowNodeTypes.END);
  }

  if (isSourceEntry) {
    const filteredTypes = [FlowNodeTypes.BRANCH, FlowNodeTypes.JUMP];
    for (const type of filteredTypes) {
      const idx = availableNodeTypes.indexOf(type);
      if (idx !== -1) availableNodeTypes.splice(idx, 1);
    }
  }

  const availableNodes = Object.fromEntries(
    Object.entries(FlowNodesData)
      .filter(([key]) => availableNodeTypes.includes(key as FlowNodeTypes))
      .sort(
        ([keyA], [keyB]) =>
          availableNodeTypes.indexOf(keyA as FlowNodeTypes) -
          availableNodeTypes.indexOf(keyB as FlowNodeTypes),
      ),
  );

  useEffect(() => {
    setIsPasteAvailable(bufferedNodes.length > 0);
  }, [bufferedNodes]);

  availableNodes.PASTE = {
    name: "Paste",
    blueprint: {},
    icon: ClipboardPaste,
    styles: {
      iconColor: TailwindColors.violet.DEFAULT,
    },
  };

  useEffect(() => {
    // Disable paste if the target is not a placeholder and the copied node is a jump
    const nodeToPasteType = bufferedNodes?.[0]?.type;
    if (
      nodeToPasteType === FlowNodeTypes.JUMP ||
      nodeToPasteType === FlowNodeTypes.END
    )
      setIsPasteAvailable(isTargetPlaceholder);
  }, [isTargetPlaceholder, bufferedNodes]);

  if (flowMode === FlowMode.LIVE) return null;

  return (
    <div className="w-8 border-2 border-[#fafafa] border-transparent bg-transparent absolute flex items-center flex-col h-auto gap-0.5 z-10 cursor-auto transform-none -top-4 -left-4 pointer-events-auto rounded-md group">
      {/* Line cover span */}
      <span className="absolute top-0 bottom-0 m-auto w-1 h-full bg-[#fafafa] z-0"></span>
      <span className="absolute top-0 bottom-0 m-auto w-full h-1 bg-[#fafafa] z-0"></span>
      <div
        className={`w-8 h-8 z-10 shrink-0 border border-border text-2xl text-secondary flex items-center justify-center cursor-pointer rounded-md transition-all group-hover:transition-all group-hover:bg-white group-hover:rounded-3xl group-hover:border-border ${isTargetPlaceholder ? "bg-white" : "border-transparent bg-transparent"} `}
      >
        <Plus size="18" />
      </div>

      <AnimatePresence>
        {isMenuVisible && (
          <motion.div
            key="settings"
            initial={{opacity: 0, scale: 0.95}}
            animate={{opacity: 1, scale: 1}}
            exit={{opacity: 0, scale: 0.95}}
            transition={{duration: 0.1, ease: "easeInOut"}}
            className="absolute z-100 left-full top-1/2 !-translate-y-1/2 ml-3"
            ref={menuRef}
          >
            <div className="bg-white border border-border shadow-lg rounded-md p-1">
              <div className="absolute right-full top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-white rotate-45 border-b border-l border-border"></div>
              {Object.entries(availableNodes).map(([type, node], index) => {
                const isPaste = type === "PASTE";
                if (isPaste && !isPasteAvailable) return null;
                const hasBooking = getNodes().some(
                  n => n.type === FlowNodeTypes.BOOKING,
                );
                const isBookingChoice =
                  type === FlowNodeTypes.BOOKING ||
                  (isPaste && bufferedNodes[0]?.type === FlowNodeTypes.BOOKING);
                const disabledByBookingLimit = isBookingChoice && hasBooking;

                return (
                  <Tooltip key={type}>
                    <TooltipTrigger asChild>
                      <div
                        onClick={() => {
                          if (isDemoMode || disabledByBookingLimit) return;

                          if (!isPaste || isPasteAvailable) {
                            onNewNodeSelect(
                              isPaste ? bufferedNodes[0] : node.blueprint,
                            );
                          }
                          handleSave();
                        }}
                        className={`text-sm flex items-center gap-2 px-2 py-1.5 rounded-md whitespace-nowrap ${
                          isDemoMode || disabledByBookingLimit
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer hover:bg-slate-50"
                        }`}
                      >
                        <node.icon
                          className={`w-4 h-4`}
                          color={node.styles.iconColor}
                        />
                        {node.name}
                      </div>
                    </TooltipTrigger>
                    {disabledByBookingLimit && (
                      <TooltipContent side="right" sideOffset={6}>
                        Only one booking node is allowed.
                      </TooltipContent>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(NewNodeHandle);
